// src/components/notifications/NotificationBadge.tsx
import React from 'react';
import { 
  ActionIcon, 
  Indicator, 
  Tooltip,
  Text 
} from '@mantine/core';
import { IconBell, IconBellFilled } from '@tabler/icons-react';
import { useNotifications } from '../../contexts/NotificationContext';

interface NotificationBadgeProps {
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'subtle' | 'filled' | 'outline' | 'light' | 'default';
  showTooltip?: boolean;
}

export function NotificationBadge({ 
  onClick, 
  size = 'md', 
  variant = 'subtle',
  showTooltip = true 
}: NotificationBadgeProps) {
  const { unreadCount } = useNotifications();
  
  const hasUnread = unreadCount > 0;
  const displayCount = unreadCount > 99 ? '99+' : unreadCount.toString();
  
  const button = (
    <Indicator
      inline
      disabled={!hasUnread}
      label={displayCount}
      size={16}
      color="red"
      position="top-end"
      offset={2}
    >
      <ActionIcon
        variant={variant}
        size={size}
        onClick={onClick}
        color={hasUnread ? 'blue' : 'gray'}
        aria-label={`Notifiche${hasUnread ? ` (${unreadCount} non lette)` : ''}`}
      >
        {hasUnread ? (
          <IconBellFilled size="1.2rem" />
        ) : (
          <IconBell size="1.2rem" />
        )}
      </ActionIcon>
    </Indicator>
  );

  if (!showTooltip) {
    return button;
  }

  return (
    <Tooltip 
      label={
        hasUnread 
          ? `${unreadCount} notifiche non lette` 
          : 'Nessuna notifica non letta'
      }
      position="bottom"
    >
      {button}
    </Tooltip>
  );
}

// Variant for text-based display (useful for mobile)
interface NotificationTextBadgeProps {
  onClick?: () => void;
  showCount?: boolean;
}

export function NotificationTextBadge({ onClick, showCount = true }: NotificationTextBadgeProps) {
  const { unreadCount } = useNotifications();
  
  return (
    <Text
      component="button"
      onClick={onClick}
      style={{ 
        background: 'none', 
        border: 'none', 
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}
      c={unreadCount > 0 ? 'blue' : 'dimmed'}
    >
      {unreadCount > 0 ? (
        <IconBellFilled size="1rem" />
      ) : (
        <IconBell size="1rem" />
      )}
      Notifiche
      {showCount && unreadCount > 0 && (
        <Text 
          component="span" 
          size="xs" 
          c="red" 
          fw={600}
        >
          ({unreadCount > 99 ? '99+' : unreadCount})
        </Text>
      )}
    </Text>
  );
}